import React from "react";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import Modal from "@/components/ui/modal";
import {
  CheckCircle,
  Download,
  Printer,
  Mail,
  FileText,
  AlertTriangle,
  Loader2,
  X,
} from "lucide-react";

const PaymentSuccessModal = ({
  show,
  onHide,
  cart,
  currencySymbol,
  pdfGenerating,
  pdfGenerated,
  pdfBlobUrl,
  currentSaleData,
  pdfError,
  buttonLoading,
  downloadPDF,
  printPDF,
  sendEmail,
  generateReceipt,
  receiptGenerating,
}) => {
  return (
    <Modal
      open={show}
      onOpenChange={(open) => !open && onHide()}
      title="Payment Successful"
      icon={CheckCircle}
      maxWidth="max-w-[500px]"
      footer={
        <Button
          onClick={onHide}
          className="w-full py-6 text-lg font-bold shadow-lg shadow-primary/20"
        >
          Done & New Sale
        </Button>
      }
    >
      <div className="text-center space-y-6">
        <div className="space-y-1">
          <p className="text-muted-foreground text-sm uppercase font-bold tracking-wider">
            Invoice #{cart.cart_number}
          </p>
          <p className="text-4xl font-extrabold text-primary">
            {currencySymbol}
            {parseFloat(cart.total || 0).toFixed(2)}
          </p>
          <p className="text-sm text-muted-foreground">
            Transaction completed successfully
          </p>
        </div>

        <div className="border-t pt-6 space-y-4">
          {pdfGenerating && (
            <div className="flex items-center justify-center gap-3 py-4 text-muted-foreground animate-pulse">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="font-medium text-sm">
                Generating PDF invoice...
              </span>
            </div>
          )}

          {!pdfGenerating && pdfGenerated && pdfBlobUrl && (
            <>
              <Alert className="bg-emerald-50 border-emerald-200 text-emerald-800 text-left">
                <FileText className="h-4 w-4 text-emerald-600" />
                <AlertTitle className="font-bold">Ready to Print</AlertTitle>
                <AlertDescription className="text-xs">
                  PDF invoice generated successfully! You can download or print
                  it now.
                </AlertDescription>
              </Alert>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <Button
                  onClick={downloadPDF}
                  className="w-full gap-2 font-bold"
                >
                  <Download className="h-4 w-4" />
                  Download PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={printPDF}
                  className="w-full gap-2 font-bold"
                >
                  <Printer className="h-4 w-4" />
                  Print A4
                </Button>
                <Button
                  variant="outline"
                  onClick={() => generateReceipt(currentSaleData?.id)}
                  className="w-full gap-2 font-bold sm:col-span-2"
                  disabled={receiptGenerating}
                >
                  {receiptGenerating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Printer className="h-4 w-4" />
                  )}
                  Print Thermal Receipt
                </Button>

                {Boolean(
                  (currentSaleData?.customer?.email || cart?.customer?.email) &&
                  (currentSaleData?.customer?.email || cart?.customer?.email) !== "" &&
                  (currentSaleData?.customer?.email || cart?.customer?.email) !== "-"
                ) && (
                    <Button
                      variant="secondary"
                      className="w-full sm:col-span-2 gap-2 font-bold"
                      onClick={() => sendEmail(currentSaleData)}
                      disabled={buttonLoading}
                    >
                      {buttonLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Sending Invoice...
                        </>
                      ) : (
                        <>
                          <Mail className="h-4 w-4" />
                          Send Invoice Email to{" "}
                          {currentSaleData?.customer?.full_name ||
                            currentSaleData?.customer?.company ||
                            currentSaleData?.customer?.name ||
                            cart?.customer?.full_name ||
                            cart?.customer?.name ||
                            "Customer"}
                        </>
                      )}
                    </Button>
                  )}
              </div>
            </>
          )}

          {pdfError && !pdfGenerating && (
            <div className="space-y-3">
              <Alert variant="destructive" className="text-left">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Generation Failed</AlertTitle>
                <AlertDescription className="text-xs">
                  PDF generation failed. You can retrieve it later from the sales
                  list.
                </AlertDescription>
              </Alert>

              {Boolean(
                (currentSaleData?.customer?.email || cart?.customer?.email) &&
                (currentSaleData?.customer?.email || cart?.customer?.email) !== "" &&
                (currentSaleData?.customer?.email || cart?.customer?.email) !== "-"
              ) && (
                  <Button
                    variant="secondary"
                    className="w-full gap-2 font-bold"
                    onClick={() => sendEmail(currentSaleData)}
                    disabled={buttonLoading}
                  >
                    {buttonLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending Invoice...
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4" />
                        Send Invoice Email to{" "}
                        {currentSaleData?.customer?.full_name ||
                          currentSaleData?.customer?.company ||
                          currentSaleData?.customer?.name ||
                          cart?.customer?.full_name ||
                          cart?.customer?.name ||
                          "Customer"}
                      </>
                    )}
                  </Button>
                )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

PaymentSuccessModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  cart: PropTypes.object.isRequired,
  currencySymbol: PropTypes.string,
  pdfGenerating: PropTypes.bool,
  pdfGenerated: PropTypes.bool,
  pdfBlobUrl: PropTypes.string,
  currentSaleData: PropTypes.object,
  pdfError: PropTypes.string,
  buttonLoading: PropTypes.bool,
  downloadPDF: PropTypes.func.isRequired,
  printPDF: PropTypes.func.isRequired,
  sendEmail: PropTypes.func,
  generateReceipt: PropTypes.func,
  receiptGenerating: PropTypes.bool,
};

export default PaymentSuccessModal;
